'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { LuArrowRight, LuCheck, LuRefreshCw } from 'react-icons/lu'

import { Button, buttonStyles } from '@/core/components/form/button/button'
import ErrorToast from '@/core/components/error-toast'
import { Combobox } from '@/core/components/form/combobox'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'

import { QUEST_STEPS, TRACK_LABEL } from '@/features/studio-renew/quests'
import {
  claimCreatorQuest,
  selectCreatorQuestTargets,
} from '@/features/studio-renew/services/creator-quest.service'
import { STEPS } from '@/features/studio-renew/steps'
import {
  useRenewStore,
  useStudioServerSnapshot,
} from '@/features/studio-renew/store'
import { useCreatorQuest } from '@/features/studio-renew/hooks/use-creator-quest'
import {
  ensureStudioCollections,
  flushAll,
  useStudioCollectionStatus,
} from '@/features/studio-renew/sync'
import { ApiError } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { CreatorQuestItem } from '@/features/studio-renew/schemas/creator-quest.schema'
import type { QuestTrack } from '@/features/studio-renew/quests'
import type { Route } from 'next'
import type { ReactNode } from 'react'

type Receipt = { ids: string[]; version: number; requestId: string }
const claimReceipts = new Map<string, Receipt>()
const receiptKey = (owner: string, identity: number) => `${owner}:${identity}`
function pendingClaim(owner: string, identity: number): Receipt | null {
  return claimReceipts.get(receiptKey(owner, identity)) ?? null
}

function Reward({ item }: { item: CreatorQuestItem }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-18 flex-none items-center gap-1.5 text-c15 font-bold tabular-nums over10:text-c16',
        item.claimable ? 'text-sub' : 'text-description'
      )}
    >
      {item.reward.gem !== undefined && (
        <>
          <span aria-hidden="true">💎</span>
          {item.reward.gem.toLocaleString('ko-KR')}
        </>
      )}
      {!!item.reward.tickets && (
        <span className="text-c12 over10:text-c14">
          해금권 {item.reward.tickets}
        </span>
      )}
    </span>
  )
}

export default function CreatorQuests({
  tab,
  onCreate,
}: {
  tab: 'works' | QuestTrack
  onCreate: (kind: QuestTrack | 'works') => void
}) {
  const initial = useStudioServerSnapshot()
  const {
    data,
    error: readError,
    refresh: refreshQuest,
    update,
  } = useCreatorQuest(initial?.scope ?? null)
  const owner = useRenewStore((state) => state.owner)
  const identity = useSessionStore((state) => state.identity)
  const memberReady = useSessionStore(
    (state) =>
      state.data?.user?.id === owner && state.data?.user?.guest === false
  )
  const characters = useRenewStore((state) => state.characters)
  const worlds = useRenewStore((state) => state.worlds)
  const commands = useRenewStore((state) => state.commands)
  const statuses = useRenewStore((state) => state.statuses)
  const chats = useRenewStore((state) => state.chats)
  const loaded = useStudioCollectionStatus((state) => state.byKind)
  const [loadingTrack, setLoadingTrack] = useState<QuestTrack | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState<Receipt | null>(null)
  const lock = useRef(false)
  const alive = useRef(false)
  const memberScope = memberReady ? `${owner}:${identity}` : null
  const [seenMemberScope, setSeenMemberScope] = useState<string | null>(null)
  if (seenMemberScope !== memberScope) {
    setSeenMemberScope(memberScope)
    if (memberScope) {
      setError('')
      if (owner) setRetry(pendingClaim(owner, identity))
    }
  }

  useEffect(() => {
    if (!memberReady) return
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [owner, identity, memberReady])

  const refresh = async () => {
    if (lock.current || !memberReady) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      await flushAll()
      await refreshQuest()
    } catch (cause) {
      if (alive.current)
        setError(
          cause instanceof Error ? cause.message : '진행을 확인하지 못했어요.'
        )
    } finally {
      lock.current = false
      if (alive.current) setBusy(false)
    }
  }

  const selectTarget = async (track: QuestTrack, id: string) => {
    if (lock.current || !memberReady || !id) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      const result = await selectCreatorQuestTargets({
        targets: { [track]: id },
      })
      if (alive.current) await update(result)
    } catch (cause) {
      if (alive.current)
        setError(
          cause instanceof Error
            ? cause.message
            : '진행 대상을 바꾸지 못했어요.'
        )
    } finally {
      lock.current = false
      if (alive.current) setBusy(false)
    }
  }

  const claim = async (item?: CreatorQuestItem) => {
    if (lock.current || !memberReady || !owner || !data?.enabled) return
    lock.current = true
    setBusy(true)
    setError('')
    try {
      const saved = pendingClaim(owner, identity)
      setRetry(saved)
      if (saved && item && saved.ids[0] !== item.id)
        throw new Error('이전에 요청한 보상 수령부터 확인해 주세요.')
      if (!saved && !item)
        throw new Error(
          '수령 요청 기록이 바뀌었어요. 퀘스트 진행을 다시 확인해 주세요.'
        )
      const request = saved ?? {
        ids: [item!.id],
        version: data.version,
        requestId: crypto.randomUUID(),
      }
      claimReceipts.set(receiptKey(owner, identity), request)
      setRetry(request)
      const result = await claimCreatorQuest(request)
      if (
        useSessionStore.getState().identity !== identity ||
        useRenewStore.getState().owner !== owner
      )
        return
      useSessionStore.getState().applyWallet(result.wallet)
      claimReceipts.delete(receiptKey(owner, identity))
      if (alive.current) {
        setRetry(null)
        await update(result)
        toast.show(
          result.grantedIds.length
            ? '제작 보상을 받았어요'
            : '이미 수령한 보상을 확인했어요'
        )
      }
    } catch (cause) {
      if (
        cause instanceof ApiError &&
        !cause.unknownOutcome &&
        cause.status >= 400 &&
        cause.status < 500 &&
        owner
      ) {
        claimReceipts.delete(receiptKey(owner, identity))
        if (alive.current) setRetry(null)
      }
      if (alive.current)
        setError(
          cause instanceof Error
            ? cause.message
            : '보상 수령 결과를 확인하지 못했어요.'
        )
    } finally {
      lock.current = false
      if (alive.current) setBusy(false)
    }
  }

  const destination = (item: CreatorQuestItem): Route | null => {
    if (!item.targetId || !item.track) return null
    if (item.id === 'w-cast') return '/studio-renew?tab=character' as Route
    if (item.id === 'm-link' || item.id === 's-link')
      return '/studio-renew' as Route
    if (item.id === 'c-intro') {
      const work = Object.values(chats)
        .filter(
          (work) =>
            work.id !== 'new' && work.characterIds.includes(item.targetId!)
        )
        .sort((a, b) => b.updatedAt - a.updatedAt)[0]
      return work ? (`/studio-renew/work/${work.id}/starts` as Route) : null
    }
    if (item.location === 'test' && item.track === 'character')
      return `/studio-renew/character/${item.targetId}/test` as Route
    const step =
      STEPS[item.track].find((step) => step.id === item.location)?.id ??
      STEPS[item.track][0].id
    return `/studio-renew/${item.track}/${item.targetId}/${step}` as Route
  }

  const loadTargets = async (track: QuestTrack) => {
    if (!memberReady || loadingTrack) return
    setLoadingTrack(track)
    setError('')
    try {
      await ensureStudioCollections([track])
    } catch (cause) {
      if (alive.current)
        setError(cause instanceof Error ? cause.message : '목록 조회 실패')
    } finally {
      if (alive.current) setLoadingTrack(null)
    }
  }

  const problem = error || readError
  const tracks = data?.enabled
    ? data.tracks.filter((track) => tab === 'works' || track.id === tab)
    : []
  const collection = {
    character: characters,
    world: worlds,
    command: commands,
    status: statuses,
  }
  if (!data && !problem)
    return (
      <p
        role="status"
        className="mb-6 text-c13 text-description over10:text-c14"
      >
        제작 퀘스트 조회 중
      </p>
    )
  if (data && !data.enabled)
    return (
      <p className="mb-6 text-c13 text-description over10:text-c14">
        제작 퀘스트 보상은 서버에서 준비 중이에요.
      </p>
    )

  return (
    <section
      aria-label="제작 퀘스트"
      className="mb-8 border-b border-border pb-7"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-c20 font-normal over10:text-c24">
          제작 퀘스트
        </h2>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="퀘스트 진행 새로 확인"
          disabled={busy}
          onClick={() => void refresh()}
        >
          <LuRefreshCw aria-hidden="true" className="size-4" />
        </Button>
      </div>
      {retry && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center justify-between gap-2 text-c13 text-description over10:text-c14"
        >
          <p>이전에 요청한 보상 수령을 확인할 수 있어요.</p>
          <Button size="sm" disabled={busy} onClick={() => void claim()}>
            수령 결과 확인
          </Button>
        </div>
      )}
      <div className="grid gap-x-8 gap-y-5 over10:grid-cols-2">
        {tracks.map((track) => {
          const item = data?.enabled
            ? data.items.find((item) => item.id === track.currentStepId)
            : undefined
          const options = Object.values(collection[track.id]).map((entry) => ({
            value: entry.id,
            label:
              ('name' in entry ? entry.name : entry.title) || '이름 없는 재료',
          }))
          const targetsReady =
            loaded[track.id] === 'ready' ||
            !!initial?.readyKinds.includes(track.id)
          const href = item ? destination(item) : null
          const hint = QUEST_STEPS[track.id].find(
            (step) => step.id === item?.id
          )?.hint
          let action: ReactNode = null
          if (item) {
            if (item.claimable)
              action = (
                <Button
                  size="sm"
                  disabled={busy || !!retry}
                  onClick={() => void claim(item)}
                >
                  받기
                </Button>
              )
            else if (track.targetLocked && !track.targetAvailable)
              action = (
                <span className="max-w-28 text-c12 leading-relaxed text-description over10:text-c14">
                  진행하던 재료를 찾을 수 없어요. 대상을 확인해 주세요.
                </span>
              )
            else if (href && track.targetAvailable)
              action = (
                <Link
                  href={href}
                  prefetch={false}
                  className={buttonStyles({
                    variant: 'ghost',
                    size: 'sm',
                    className: 'min-h-11! flex-none text-point!',
                  })}
                >
                  하러 가기
                  <LuArrowRight aria-hidden="true" className="size-4" />
                </Link>
              )
            else if (item.id === 'c-intro' && track.targetAvailable)
              action = (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => onCreate('works')}
                >
                  작품 만들기
                </Button>
              )
            else if (!options.length)
              action = (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => onCreate(track.id)}
                >
                  재료 만들기
                </Button>
              )
            else
              action = (
                <span className="max-w-24 text-c12 leading-relaxed text-description over10:text-c14">
                  진행할 재료를 골라 주세요
                </span>
              )
          }
          return (
            <div key={track.id} className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-c13 font-semibold text-description over10:text-c14">
                  {TRACK_LABEL[track.id]}
                </h3>
                {targetsReady
                  ? !!options.length && (
                      <Combobox
                        ariaLabel={`${TRACK_LABEL[track.id]} 진행 대상`}
                        variant="ghost"
                        value={track.targetId ?? ''}
                        placeholder="진행 대상"
                        options={options}
                        disabled={busy || !memberReady || track.targetLocked}
                        onChange={(id) => void selectTarget(track.id, id)}
                        className="max-w-48"
                      />
                    )
                  : !track.targetLocked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={loadingTrack === track.id}
                        disabled={busy || !memberReady || !!loadingTrack}
                        onClick={() => void loadTargets(track.id)}
                      >
                        대상 선택
                      </Button>
                    )}
              </div>
              {item ? (
                <div
                  key={item.id}
                  className="flex min-h-16 items-center gap-3 motion-safe:animate-fade-in"
                >
                  <Reward item={item} />
                  <div className="min-w-0 flex-1">
                    <p className="text-c15 font-semibold over10:text-c16">
                      {item.title}
                    </p>
                    {hint && (
                      <p className="mt-1 text-c13 leading-relaxed text-description over10:text-c14">
                        {hint}
                      </p>
                    )}
                  </div>
                  {action}
                </div>
              ) : (
                <p className="flex min-h-16 items-center gap-2 text-c14 text-description">
                  <LuCheck aria-hidden="true" className="size-4 text-ok" />이
                  트랙의 제작 퀘스트를 마쳤어요.
                </p>
              )}
            </div>
          )
        })}
      </div>
      {tab === 'works' && data?.enabled && (
        <details className="mt-5 border-t border-border pt-3">
          <summary className="cursor-pointer py-2 text-c14 font-semibold">
            작가 업적
          </summary>
          <div className="divide-y divide-border">
            {data.items
              .filter((item) => item.kind === 'achievement')
              .map((item) => (
                <div
                  key={item.id}
                  className="flex min-h-16 items-center gap-3 py-2"
                >
                  <Reward item={item} />
                  <p className="min-w-0 flex-1 text-c14">
                    {item.title}
                    <span className="ml-2 text-c12 text-description tabular-nums over10:text-c14">
                      {item.progress.toLocaleString('ko-KR')} /{' '}
                      {item.target.toLocaleString('ko-KR')}
                    </span>
                  </p>
                  {item.claimed ? (
                    <span className="text-c13 text-description over10:text-c14">
                      수령 완료
                    </span>
                  ) : (
                    <Button
                      variant={item.claimable ? 'primary' : 'secondary'}
                      size="sm"
                      disabled={!item.claimable || busy || !!retry}
                      onClick={() => void claim(item)}
                    >
                      받기
                    </Button>
                  )}
                </div>
              ))}
          </div>
        </details>
      )}
      <ErrorToast message={problem} />
    </section>
  )
}
